import t from "@/lang/lang";
import { ServiceObjectAddress } from "@/services/backend/customers/components/service-object-address";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ListCommitmentsOrderBy,
  ListCommitmentsRequest,
  UpdateCommitmentStatusRequest,
  useRelationshipsRelationshipDeleteCommitmentsMutation,
  useRelationshipsRelationshipListCommitmentsQuery,
  useRelationshipsRelationshipShowQuery,
  useRelationshipsRelationshipUpdateCommitmentStatusMutation,
} from "@/services/backend/relationships/api/service";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { weekRFC3339 } from "@/shared/lib/utilities/date";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { DateBetweenFilter } from "@/shared/components/filter/date-between-filter";
import { Button } from "@/shared/components/ui/button";
import {
  Commitment,
  CommitmentStatus,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Trash2 } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { cn } from "@/shared/lib/utils";
import { Separator } from "@/shared/components/ui/separator";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { ReactNode, useEffect, useState } from "react";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { toast } from "sonner";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { BranchFilter } from "@/services/backend/branch/components/branch-filter";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { ServiceObjectFilter } from "@/services/backend/customers/components/service-object-filter";
import { ServiceKindFilter } from "@/services/backend/services/components/service-kind-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { FulfillmentCell } from "@/services/backend/relationships/components/fulfillment-cell";
import { CommitmentStatusFilter } from "@/services/backend/relationships/components/commitment-state-filter";
import { TableCard } from "@/shared/components/tables/table-card";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { CommitmentStatusSelect } from "@/services/backend/relationships/components/commitment-status-select";
import { CreateSeriesDialog } from "@/services/backend/relationships/components/create-series-dialog";
import { v4 } from "uuid";

export function CommitmentsTable({
  request,
  onRequestChange,
  selection,
  onSelectionChange,
  highlightAsCurrent,
  skipLoading = false,
  showBorder = false,
  hideServiceObject = true,
  renderCommitmentLink,
  renderDocumentLink,
  renderServiceObjectLink,
}: {
  request: ListCommitmentsRequest;
  onRequestChange: (request: ListCommitmentsRequest) => void;
  selection: Commitment[];
  onSelectionChange: (selection: Commitment[]) => void;
  highlightAsCurrent?: UUID;
  skipLoading?: boolean;
  showBorder?: boolean;
  hideServiceObject?: boolean;
  renderCommitmentLink: (
    commitment: Commitment,
    relationship: Relationship,
    children: ReactNode,
  ) => ReactNode;
  renderDocumentLink: (path: string, children: ReactNode) => ReactNode;
  renderServiceObjectLink?: (serviceObjectId: string) => ReactNode;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useRelationshipsRelationshipListCommitmentsQuery(request, {
    skip: skipLoading,
  });
  const emptyList =
    skipLoading || (!isLoading && !error && list!.commitments.length === 0);

  return (
    <div className="grid gap-2">
      <TableCard className={cn(!showBorder && "border-none shadow-none")}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 px-0 text-center">
                <AllCommitmentsCheckbox
                  commitments={list?.commitments}
                  selection={selection}
                  onSelectionChange={onSelectionChange}
                />
              </TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Soll KW")}</TableHead>
              <TableHead hidden={hideServiceObject}>{t("Objekt")}</TableHead>
              <TableHead>{t("Dienstleistung")}</TableHead>
              <TableHead>{t("Turnus")}</TableHead>
              <TableHead>{t("Erfüllung")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={5} />}
            {error && <TableRtkErrorRow error={error} colSpan={5} />}
            {emptyList && <TableEmptyRow colSpan={5} />}
            {list?.commitments.map((commitment) => (
              <CommitmentRow
                key={commitment.id}
                commitment={commitment}
                highlightAsCurrent={highlightAsCurrent}
                selection={selection}
                onSelectionChange={onSelectionChange}
                hideServiceObject={hideServiceObject}
                renderCommitmentLink={renderCommitmentLink}
                renderDocumentLink={renderDocumentLink}
                renderServiceObjectLink={renderServiceObjectLink}
              />
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) => {
            onRequestChange({ ...request, limit, offset });
          }}
          className="ml-auto"
        />
      )}
    </div>
  );
}

function CommitmentRow({
  commitment,
  highlightAsCurrent,
  selection,
  onSelectionChange,
  hideServiceObject = true,
  renderCommitmentLink,
  renderDocumentLink,
  renderServiceObjectLink,
}: {
  commitment: Commitment;
  highlightAsCurrent?: UUID;
  selection: Commitment[];
  onSelectionChange: (selection: Commitment[]) => void;
  hideServiceObject?: boolean;
  renderCommitmentLink: (
    commitment: Commitment,
    relationship: Relationship,
    children: ReactNode,
  ) => ReactNode;
  renderDocumentLink: (path: string, children: ReactNode) => ReactNode;
  renderServiceObjectLink?: (serviceObjectId: string) => ReactNode;
}) {
  const { data: relationship } = useRelationshipsRelationshipShowQuery({
    id: commitment.relationshipId,
  });

  if (!relationship) {
    return null;
  }

  return (
    <TableRow
      className={cn(highlightAsCurrent === commitment.id && "bg-muted")}
    >
      <TableCell className="px-0 text-center">
        <CommitmentCheckbox
          commitment={commitment}
          selection={selection}
          onSelectionChange={onSelectionChange}
        />
      </TableCell>
      <TableCell>
        <CommitmentStatusCell commitment={commitment} />
      </TableCell>
      <TableCell>
        <CommitmentLink
          commitment={commitment}
          relationship={relationship}
          renderCommitmentLink={renderCommitmentLink}
        />
      </TableCell>
      <TableCell className="space-x-2" hidden={hideServiceObject}>
        {renderServiceObjectLink?.(relationship.serviceObjectId)}
        <ServiceObjectAddress id={relationship.serviceObjectId} />
      </TableCell>
      <TableCell>{commitment.serviceKind}</TableCell>
      <TableCell>{commitment.cycle}</TableCell>
      <TableCell>
        <FulfillmentCell
          relationship={relationship}
          commitment={commitment}
          renderDocumentLink={renderDocumentLink}
        />
      </TableCell>
    </TableRow>
  );
}

export function AllCommitmentsCheckbox({
  commitments,
  selection,
  onSelectionChange,
}: {
  commitments?: Commitment[];
  selection: Commitment[];
  onSelectionChange: (selection: Commitment[]) => void;
}) {
  if (!commitments) {
    return <Checkbox disabled />;
  }

  const allSelected = commitments.every((c) =>
    selection.some((sc) => sc.id === c.id),
  );

  return (
    <Checkbox
      checked={allSelected}
      onCheckedChange={(checked) => {
        const newSelection = checked ? commitments : [];

        onSelectionChange(newSelection);
      }}
    />
  );
}

export function CommitmentCheckbox({
  commitment,
  selection,
  onSelectionChange,
}: {
  commitment: Commitment;
  selection: Commitment[];
  onSelectionChange: (selection: Commitment[]) => void;
}) {
  return (
    <Checkbox
      checked={selection.some((sc) => sc.id === commitment.id)}
      onCheckedChange={(checked) => {
        const newSelection = checked
          ? [...selection, commitment]
          : selection.filter((sc) => sc.id !== commitment.id);

        onSelectionChange(newSelection);
      }}
    />
  );
}

export function CommitmentStatusCell({
  commitment,
}: {
  commitment: Commitment;
}) {
  const [request, setRequest] = useState<UpdateCommitmentStatusRequest>({
    id: commitment.relationshipId,
    commitmentId: commitment.id,
    status: commitment.status,
  });
  const mutation = useRelationshipsRelationshipUpdateCommitmentStatusMutation();
  const { isLoading } = useDebouncedMutation(
    request,
    setRequest,
    mutation,
    {
      id: commitment.relationshipId,
      commitmentId: commitment.id,
      status: commitment.status,
    },
    { delay: 0 },
  );

  return (
    <CommitmentStatusSelect
      triggerClassName="h-6 border-none shadow-none cursor-pointer"
      value={request.status}
      onValueChange={(status) =>
        setRequest({ ...request, status: status as CommitmentStatus })
      }
      disabled={isLoading}
    />
  );
}

export function CommitmentLink({
  commitment,
  relationship,
  renderCommitmentLink,
}: {
  commitment: Commitment;
  relationship: Relationship;
  renderCommitmentLink: (
    commitment: Commitment,
    relationship: Relationship,
    children: ReactNode,
  ) => ReactNode;
}) {
  return (
    <Button asChild variant="link" size="sm" className="px-0 whitespace-normal">
      {renderCommitmentLink(
        commitment,
        relationship,
        <>KW {weekRFC3339(commitment.date)}</>,
      )}
    </Button>
  );
}

export function CommitmentsTableFilters({
  request,
  onRequestChange,
  selection,
  onSelectionChange,
  hideCompanyFilter = true,
  hideBranchFilter = true,
  hideCustomerFilter = true,
  hideServiceObjectFilter = true,
  hideServiceKindFilter = true,
  hideCreateSeries = false,
}: {
  request: ListCommitmentsRequest;
  onRequestChange: (request: ListCommitmentsRequest) => void;
  selection: Commitment[];
  onSelectionChange: (selection: Commitment[]) => void;
  hideCompanyFilter?: boolean;
  hideBranchFilter?: boolean;
  hideCustomerFilter?: boolean;
  hideServiceObjectFilter?: boolean;
  hideServiceKindFilter?: boolean;
  hideCreateSeries?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <CommitmentStatusFilter
        filter={request.status}
        onFilterChange={(status) => {
          onRequestChange({ ...request, status });
        }}
      />
      <CompanyFilter
        filter={request.company}
        onFilterChange={(company) => {
          onRequestChange({ ...request, company });
        }}
        hidden={hideCompanyFilter}
      />
      <BranchFilter
        filter={request.branch}
        onFilterChange={(branch) => onRequestChange({ ...request, branch })}
        companyIds={
          request.company?.active ? request.company.values : undefined
        }
        hidden={hideBranchFilter}
      />
      <CustomerFilter
        filter={request.customer}
        onFilterChange={(customer) => onRequestChange({ ...request, customer })}
        hidden={hideCustomerFilter}
      />
      <ServiceObjectFilter
        filter={request.serviceObject}
        onFilterChange={(serviceObject) =>
          onRequestChange({ ...request, serviceObject })
        }
        customerIds={
          request.customer?.active ? request.customer.values : undefined
        }
        hidden={hideServiceObjectFilter}
      />
      <ServiceKindFilter
        filter={request.serviceKind}
        onFilterChange={(serviceKind) =>
          onRequestChange({ ...request, serviceKind })
        }
        hidden={hideServiceKindFilter}
      />
      <DateBetweenFilter
        label={t("Datum")}
        filter={request.date}
        onFilterChange={(date) => onRequestChange({ ...request, date })}
      />
      <OrderBySelect<ListCommitmentsOrderBy>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Dienstleistung"), value: "serviceKind" },
          { label: t("Status"), value: "status" },
          {
            label: t("Kunde (gruppieren)"),
            value: "customer",
          },
          { label: t("Turnus"), value: "cycle" },
          { label: t("Soll KW"), value: "date" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Bearbeitet am"), value: "updatedAt" },
        ]}
      />
      <Separator orientation="vertical" />
      <CreateSeriesDialog
        key={selection[0]?.id ?? v4()}
        commitment={selection[0]}
        disabled={selection.length !== 1}
        hidden={hideCreateSeries}
      />
      <DeleteCommitmentsDialog
        commitments={selection}
        onSuccess={() => onSelectionChange([])}
      />
    </div>
  );
}

function DeleteCommitmentsDialog({
  commitments,
  onSuccess,
}: {
  commitments: Commitment[];
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [deleteCommitments, { isLoading, error, reset, isSuccess }] =
    useRelationshipsRelationshipDeleteCommitmentsMutation();

  useEffect(() => {
    if (isSuccess) {
      reset();
      toast.success(`${commitments.length} Sollstellungen gelöscht`);
      setOpen(false);
      onSuccess();
    }
  }, [commitments.length, isSuccess, onSuccess, reset]);

  const doDelete = () => {
    if (isLoading) return;

    deleteCommitments({
      commitments: commitments.map((c) => ({
        relationshipId: c.relationshipId,
        commitmentId: c.id,
      })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={Trash2}
        label=""
        variant="destructive"
        size="sm"
        disabled={commitments.length === 0}
      />
      <DialogContent>
        <CommonDialogHeader
          title={t(
            `${commitments.length} Sollstellung${commitments.length > 1 ? "en" : ""} löschen?`,
          )}
          description={t(
            `Ein Klick auf Betätigen löscht die Sollstellung${commitments.length > 1 ? "en" : ""}.`,
          )}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doDelete}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
